/**
 * MERGED-CONTRACT-005: shared error-envelope type contract.
 *
 * Every non-2xx response from `apps/api` is shaped as:
 *
 *   {
 *     error: {
 *       code: <stable machine-readable identifier from api-error-codes.ts>,
 *       message: <human-readable for developers>,
 *       requestId: <correlation handle, always non-empty>,
 *       details?: <structured payload, optional>
 *     }
 *   }
 *
 * The shape is enforced in production by `apps/api/src/middleware/error-
 * envelope.ts`. It's consumed by `apps/web/src/lib/tinkr-api/client.ts`
 * (via `TinkrApiError`). The contract used to live in two places — once
 * in `apps/api/src/lib/errors.ts` and once inline in the web client. A
 * silent drift between them would mean the web client misreads a field
 * the API actually populates (or vice versa), and the failure mode is a
 * `requestId` shown as "unknown" or `details` going unread.
 *
 * This module defines the contract once. Both apps import it. Vitest
 * parity tests in each app assert that their concrete fixture envelopes
 * round-trip through the shared shape.
 *
 * Why no runtime Zod schema yet? `packages/shared/` doesn't currently
 * depend on any runtime validation library and adding one is a separate
 * architectural decision. Static types + per-app parity tests cover the
 * regression surface today. Future PR can promote this to a Zod schema
 * if both apps add the dependency.
 */

/**
 * Optional structured payload for an error response. Each `code` defines
 * its own `details` shape in `api-error-codes.ts` / per-route docs. The
 * shared envelope cannot enforce the per-code shape (that would require
 * a discriminated union over every code), so `details` stays a typed
 * map of unknown values here. Callers should narrow at the use site
 * based on `error.code`.
 */
export type ErrorEnvelopeDetails = Record<string, unknown>;

/**
 * The full non-2xx response body shape. Use this in both the API
 * middleware (`registerErrorEnvelope`) and the web client
 * (`TinkrApiError`).
 *
 * `requestId` MUST be non-empty. The middleware generates a fresh UUID
 * when no request-id context is set, so clients can always quote a
 * correlation handle to support.
 */
export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    requestId: string;
    details?: ErrorEnvelopeDetails;
  };
}

/**
 * Narrowing predicate for callers that receive an unknown body shape
 * (e.g., the web client's raw fetch response). Returns true if the body
 * looks like a Tinkr error envelope.
 *
 * This is deliberately structural (and intentionally NOT a full Zod
 * validator): the goal is to safely distinguish "this is an error
 * envelope I can read fields off of" from "this is some other JSON
 * blob." Per-field validation belongs at the response-parsing layer.
 */
export function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as { error?: unknown };
  if (candidate.error === null || typeof candidate.error !== "object") {
    return false;
  }
  const err = candidate.error as {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
  };
  return (
    typeof err.code === "string" &&
    typeof err.message === "string" &&
    typeof err.requestId === "string" &&
    err.requestId.length > 0
  );
}
